/**
 * Canonical permission registry (ADR-0056). The single source of truth
 * for every permission code on every sidebar item.
 *
 * Both the API (seeds `permissions` rows from this) AND the admin
 * (renders the matrix + gates nav items via `requirePermission`) read
 * from THIS file. Anywhere else that lists permissions = drift bug.
 *
 * Design:
 *   - `GROUPS` mirrors the 8 sidebar groups in `apps/admin/src/app/
 *     nav-config.tsx`. The `id` matches `NavGroupConfig.id` 1:1 so
 *     the matrix and the sidebar render in the same order.
 *   - Each group has N modules. A module maps to ONE sidebar nav
 *     item (`navItemId` matches `NavItemConfig.id` 1:1).
 *   - Each module declares its actions ("read", "write", "delete").
 *     The full permission code is `${moduleKey}.${action}`, e.g.
 *     `crm.query_statuses.write`.
 *
 * Backward-compat:
 *   - Legacy coarse codes (`master.read`, `master.write`, `lead.read`,
 *     `lead.create`, `customer.read`, `blogs.write`, `pages.write`,
 *     `navigation.write`, `settings.write`, `user.read`,
 *     `itinerary.read`, `itinerary.create`, `itinerary.update`) stay
 *     seeded as ALIASES. The migration backfills any role holding a
 *     legacy code with the equivalent granular set (`master.read` →
 *     all `<group>.<module>.read` for crm/inv/itin/geo).
 *   - The Permission rows for those legacy codes are NOT deleted.
 *     Any hardcoded server check on `master.read` keeps working.
 *
 * Adding a new sidebar item:
 *   1. Add an entry to `NAV_GROUPS` in `apps/admin/src/app/nav-config.tsx`.
 *   2. Add the matching `PermissionModuleEntry` here under the right
 *      group with matching `navItemId`.
 *   3. Add a migration that inserts the `<moduleKey>.<action>` rows
 *      into `permissions`. Roles needing the access get a grant in
 *      the same migration (or via the seed).
 */

import { z } from 'zod';

/**
 * Schema for one module entry. Frozen at runtime via `as const` below
 * so the registry is statically inspectable.
 */
export const PermissionActionSchema = z.enum(['read', 'write', 'delete']);
export type PermissionAction = z.infer<typeof PermissionActionSchema>;

export interface PermissionModuleEntry {
  /** Permission code prefix - the resource part of `<resource>.<action>`.
   *  Convention: `<group>.<noun>` lowercase snake_case. */
  moduleKey: string;
  /** Display label shown in the matrix UI. */
  label: string;
  /** Short one-line description shown beneath the label. */
  description: string;
  /** Actions this module supports. The full code is
   *  `${moduleKey}.${action}`. */
  actions: ReadonlyArray<PermissionAction>;
  /** Sidebar nav item id this module gates (1:1 with
   *  `NavItemConfig.id`). Optional for modules that don't surface as
   *  a sidebar item (none today, but reserved). */
  navItemId?: string;
}

export interface PermissionGroupEntry {
  /** Group id - matches `NavGroupConfig.id` 1:1. */
  id: string;
  /** Display label rendered as the matrix group header. */
  label: string;
  /** One-line description shown beneath the group header. */
  description: string;
  modules: ReadonlyArray<PermissionModuleEntry>;
}

const READ_WRITE = ['read', 'write'] as const;
const READ_WRITE_DELETE = ['read', 'write', 'delete'] as const;
const READ_ONLY = ['read'] as const;

export const PERMISSION_GROUPS: ReadonlyArray<PermissionGroupEntry> = [
  // -----------------------------------------------------------------
  // Operations - the daily work surface.
  // -----------------------------------------------------------------
  {
    id: 'operations',
    label: 'Operations',
    description: 'Daily work surface: leads, customers, itineraries, the live TBO search.',
    modules: [
      {
        moduleKey: 'ops.leads',
        label: 'Leads',
        description: 'Lead inbox, detail, transitions, follow-ups.',
        actions: READ_WRITE_DELETE,
        navItemId: 'leads',
      },
      {
        moduleKey: 'ops.lead_imports',
        label: 'Lead imports',
        description: 'Bulk CSV / XLSX import for leads (ADR-0036).',
        actions: READ_WRITE,
        navItemId: 'lead-imports',
      },
      {
        moduleKey: 'ops.scheduler',
        label: 'Scheduler',
        description: 'Cron registry + cron_runs audit log (ADR-0037).',
        actions: READ_ONLY,
        navItemId: 'scheduler',
      },
      {
        moduleKey: 'ops.status',
        label: 'Status page',
        description:
          'Live health of our apps, every third-party integration and the infrastructure, with the incident log (docs/ops/STATUS-PAGE.md). Admin only.',
        actions: READ_WRITE,
        navItemId: 'status',
      },
      {
        moduleKey: 'ops.customers',
        label: 'Customers',
        description: 'Customer master CRUD plus contacts and documents.',
        actions: READ_WRITE_DELETE,
        navItemId: 'customers',
      },
      {
        moduleKey: 'ops.itineraries',
        label: 'Itineraries',
        description: 'Itinerary lifecycle and revisions.',
        actions: READ_WRITE_DELETE,
        navItemId: 'itineraries',
      },
      {
        moduleKey: 'ops.fixed_departures',
        label: 'Fixed Departures',
        description: 'Reusable itinerary templates: cities + nights, days, and option pools.',
        actions: READ_WRITE_DELETE,
        navItemId: 'fixed-departures',
      },
      {
        moduleKey: 'ops.tbo',
        label: 'TBO live search',
        description: 'Live TBO hotel + flight inventory used while building quotes.',
        actions: READ_ONLY,
        navItemId: 'tbo',
      },
      // 2026-09-06. Website flight + hotel bookings: the list, the detail
      // (traveller documents, money, supplier timeline) and the hold queue
      // where an admin re-issues or refunds a paid booking the supplier
      // refused. Admin-only by default (ADMIN_VISIBLE_ONLY_MODULES): it
      // carries passport numbers and can move guest money, so no role picks
      // it up through the ops fan-out - an ADMIN grants it deliberately.
      {
        moduleKey: 'ops.travel_bookings',
        label: 'Website bookings',
        description:
          'Flight and hotel bookings made on the website: list, traveller details, money, supplier timeline, and the re-issue / refund actions on held bookings.',
        actions: READ_WRITE,
        navItemId: 'ticketing-action',
      },
      // 2026-07-09. Sidebar-visibility gate for the reviewer inbox that
      // handles pending lead status-change requests. Grant this only to
      // roles that should see approvals; SALES_AGENT holds `ops.leads.write`
      // (needed to submit approvals) and MUST NOT hold this code.
      {
        moduleKey: 'ops.lead_approvals',
        label: 'Lead approvals inbox',
        description: 'Reviewer inbox for lead status-change requests.',
        actions: READ_ONLY,
        navItemId: 'lead-approvals',
      },
      // 2026-07-09. Sidebar-visibility gate for the customer-facing
      // itinerary link generator. Admin-only by default.
      {
        moduleKey: 'ops.customer_pdf',
        label: 'Customer itinerary link',
        description: 'Generate shareable customer-facing itinerary PDF links.',
        actions: READ_ONLY,
        navItemId: 'customer-pdf',
      },
      // 2026-07-17. Lead parsing system - auto-parses inbound leads
      // (email / webhook / channel messages) into structured lead rows.
      // Admin-only by default (see ADMIN_VISIBLE_ONLY_MODULES): the module
      // ships in the matrix so an ADMIN can grant it to any role from the
      // /roles UI, but no existing role picks it up via the ops fan-out.
      // 2026-10-02. Read-only page gate; what a holder may DO on the page
      // comes from the four tab modules below (one per tab, like the
      // Settings tabs), each needing this one as its prerequisite
      // (PERMISSION_PREREQUISITES). The old `ops.lead_parsing.write` is
      // gone: write lives on the tabs.
      {
        moduleKey: 'ops.lead_parsing',
        label: 'Lead parsing system',
        description:
          'Opens the Lead Parsing System page and its sidebar item. Pick the tabs below for what it shows.',
        actions: READ_ONLY,
        navItemId: 'lead-parsing',
      },
      {
        moduleKey: 'ops.lps_inbound',
        label: 'Lead parsing · Inbound leads',
        description:
          'The inbound queue, summaries and lead detail. Write: retry a failed send, hand a waiting lead to an agent.',
        actions: READ_WRITE,
        navItemId: 'lead-parsing',
      },
      // 2026-10-04. The Inbound leads tab reads as three sections - All,
      // Inorganic (ad leads), Organic - like the Marketing report. One read
      // module per section; each needs `ops.lps_inbound.read` (prerequisite).
      {
        moduleKey: 'ops.lps_inbound_all',
        label: 'Lead parsing · Inbound · All',
        description: 'The whole inbound queue and both breakdown tables, every lead together.',
        actions: READ_ONLY,
        navItemId: 'lead-parsing',
      },
      {
        moduleKey: 'ops.lps_inbound_inorganic',
        label: 'Lead parsing · Inbound · Inorganic',
        description: 'Inbound queue and breakdowns for ad (paid) leads only.',
        actions: READ_ONLY,
        navItemId: 'lead-parsing',
      },
      {
        moduleKey: 'ops.lps_inbound_organic',
        label: 'Lead parsing · Inbound · Organic',
        description: 'Inbound queue and breakdowns for organic leads only.',
        actions: READ_ONLY,
        navItemId: 'lead-parsing',
      },
      {
        moduleKey: 'ops.lps_deliveries',
        label: 'Lead parsing · Deliveries',
        description: 'The webhook request log with raw provider payloads.',
        actions: READ_ONLY,
        navItemId: 'lead-parsing',
      },
      {
        moduleKey: 'ops.lps_connections',
        label: 'Lead parsing · Connections',
        description:
          'Connections and their webhook URLs. Write: create, edit, pause, rotate the secret, delete.',
        actions: READ_WRITE,
        navItemId: 'lead-parsing',
      },
      {
        moduleKey: 'ops.lps_website',
        label: 'Lead parsing · Website leads (CRM)',
        description: 'Website form leads as they landed in the CRM.',
        actions: READ_ONLY,
        navItemId: 'lead-parsing',
      },
      // 2026-07-26. Queue of leads released from departed agents
      // (deactivate / delete / date-of-exit reached). `read` shows the
      // page; `write` allows bulk-reassign to an active agent. Supervisor
      // surface: granted to sales_lead + admin only (see sales_lead
      // defaults + ADMIN_VISIBLE_ONLY_MODULES).
      {
        moduleKey: 'ops.exit_unassigned',
        label: 'Exit-unassigned leads',
        description:
          'Queue of leads released from departed agents. Reporting managers see their reports; admins see all.',
        actions: READ_WRITE,
        navItemId: 'exit-unassigned',
      },
      // 2026-07-24. Reporting module (Daily Tracker: agent-wise lead tracker,
      // pivot, pipeline). Previously hard-gated `requireRole: ['ADMIN']` with
      // no matrix entry. Now a proper read-only permission so an ADMIN can
      // grant reporting access to any role from /roles. Admin-only by default
      // (see ADMIN_VISIBLE_ONLY_MODULES) so no existing role picks it up via
      // the ops fan-out - preserves today's admin-only behaviour.
      {
        moduleKey: 'ops.lead_reports',
        label: 'Lead reports',
        description: 'Daily tracker: agent-wise lead counts, pivot, and pipeline value reports.',
        actions: READ_ONLY,
        navItemId: 'lead-reports',
      },
      // 2026-07-30. Marketing reporting module - lead attribution by source /
      // sub-source / medium and the Meta ad triplet (campaign / adset / ad).
      // Separate code from `ops.lead_reports` so marketing staff can be given
      // channel reporting without org-wide agent performance. Admin-only by
      // default (see ADMIN_VISIBLE_ONLY_MODULES); grant from /roles.
      {
        moduleKey: 'ops.marketing_reports',
        // "(page access)" like Settings: this code only opens the page. On its
        // own it shows no data - the sections below decide that, and people
        // mistook the plain "Marketing reports" row for the All section.
        label: 'Marketing reports (page access)',
        description:
          'Opens the Marketing report page. Shows no data by itself - also grant at least one section below (All, Inorganic or Organic). Write = entering cost figures (ad spend, cost heads).',
        // `write` covers entering the monthly cost inputs (management cost /
        // all-account spend); the report itself is read-only.
        actions: READ_WRITE,
        navItemId: 'marketing-reports',
      },
      // 2026-09-25. The report reads as three sections, split on the lead's
      // Query Type - All, Inorganic (paid leads, where every cost belongs) and
      // Organic (no cost side). One read module per section so an admin can
      // hand out exactly the sections a role needs, like the Settings tabs.
      // `ops.marketing_reports.read` still opens the page; a role needs it
      // plus at least one section. Admin-only by default (see
      // ADMIN_VISIBLE_ONLY_MODULES). Spend / adset / cost-head / Meta ad data
      // is readable with the All or the Inorganic section - never Organic.
      {
        moduleKey: 'ops.marketing_reports_all',
        label: 'Marketing reports · All',
        description:
          'Every lead together, with the full cost side (ad spend, cost heads, Campaigns tab).',
        actions: READ_ONLY,
        navItemId: 'marketing-reports',
      },
      {
        moduleKey: 'ops.marketing_reports_inorganic',
        label: 'Marketing reports · Inorganic',
        description:
          'Paid (Inorganic) leads only, with their ad spend, cost heads and the Campaigns tab.',
        actions: READ_ONLY,
        navItemId: 'marketing-reports',
      },
      {
        moduleKey: 'ops.marketing_reports_organic',
        label: 'Marketing reports · Organic',
        description:
          'Organic leads only. No cost data at all - spend, CPQ, RoAS and ad figures stay hidden.',
        actions: READ_ONLY,
        navItemId: 'marketing-reports',
      },
      // 2026-08-11. CallSense - AI call analytics dashboard (agent scorecards,
      // call quality scores, red flags) built on Gemini analysis of CallerDesk
      // recordings (apps/intelligence pipeline -> call_insights table).
      // Admin-only by default (see ADMIN_VISIBLE_ONLY_MODULES); grant from
      // /roles. Read-only: the dashboard has no mutating surface.
      {
        moduleKey: 'ops.callsense',
        label: 'CallSense',
        description:
          'AI call analytics: agent scorecards, call quality scores, red flags and coaching insights from analyzed call recordings.',
        actions: READ_ONLY,
        navItemId: 'callsense',
      },
      // 2026-08-10. WhatsApp chat (DoubleTick) - the floating widget + lead-page
      // button. `read` = see the widget, inbox and threads; `write` = send
      // session/template messages. No navItemId: the surface is the global
      // floating bubble, not a sidebar page. Excluded from every group
      // fan-out (see ADMIN_VISIBLE_ONLY_MODULES) and granted explicitly to
      // sales_lead + sales_agent below - matching the intended audience
      // (ADMIN / SALES_LEAD / SALES_AGENT) while staying grantable to any
      // custom role from /roles.
      {
        moduleKey: 'ops.whatsapp',
        label: 'WhatsApp chat',
        description:
          'Customer WhatsApp threads via DoubleTick: floating chat widget, inbox, and template/session sends.',
        actions: READ_WRITE,
      },
      // 2026-08-24. Contract e-signing (Documenso). Replaces the hardcoded
      // @Roles gates on ContractsController. `read` = list/view/download
      // (the lead-page Contracts tab), `write` = generate/upload/resend,
      // `delete` = void. No navItemId - the surface is the lead detail tab,
      // not a sidebar page. Default grants mirror the old role lists:
      // sales_lead holds all three, sales_agent read+write, ops all three
      // (fan-out read+write + explicit delete), finance/read_only read.
      {
        moduleKey: 'ops.contracts',
        label: 'Contracts (e-sign)',
        description:
          'Contract e-signing via Documenso: view and download contracts, generate and resend for signature, void.',
        actions: READ_WRITE_DELETE,
      },
      // 2026-10-14. Operations queue of converted leads ("Fresh converted
      // leads"): a lead lands here once the customer has uploaded the
      // post-contract documents and is dealt round-robin to an ops executive.
      // `read` = the screen, `write` = open / complete / send to ops /
      // reassign (reassign is also limited to ADMIN + OPERATIONS_LEAD in
      // code), `delete` = cancel a case.
      {
        moduleKey: 'ops.converted_leads',
        label: 'Converted leads (operations)',
        description:
          'Operations queue of converted leads with their pending flights, hotels, activities and transfers.',
        actions: READ_WRITE_DELETE,
        navItemId: 'converted-leads',
      },
      // 2026-10-19. Accounts' queue of supplier fund requests raised by ops
      // when they issue a booking. `read` = the screen, `write` = mark paid
      // and upload the payment proof. Granted to admin + finance by migration.
      {
        moduleKey: 'ops.fund_requests',
        label: 'Fund requests (accounts)',
        description:
          'Supplier payments asked by operations: pay, upload the payment screenshot, mark paid.',
        actions: READ_WRITE,
        navItemId: 'fund-requests',
      },
    ],
  },

  // -----------------------------------------------------------------
  // Inventory - the bookable catalog itineraries are built from.
  // -----------------------------------------------------------------
  {
    id: 'inventory',
    label: 'Inventory & supply',
    description: 'The bookable catalog an itinerary is assembled from.',
    modules: [
      {
        moduleKey: 'inv.hotels',
        label: 'Hotels',
        description: 'Hotel master + rooms + inventory.',
        actions: READ_WRITE_DELETE,
        navItemId: 'hotels',
      },
      {
        moduleKey: 'inv.activities',
        label: 'Activities',
        description: 'Activity master + variants.',
        actions: READ_WRITE_DELETE,
        navItemId: 'activities',
      },
      {
        moduleKey: 'inv.transfers',
        label: 'Transfers',
        description: 'Transfer master + price grid.',
        actions: READ_WRITE_DELETE,
        navItemId: 'transfers',
      },
      {
        moduleKey: 'inv.visas',
        label: 'Visas',
        description: 'Visa master.',
        actions: READ_WRITE,
        navItemId: 'visas',
      },
      {
        moduleKey: 'inv.insurance',
        label: 'Insurance',
        description: 'Travel insurance master.',
        actions: READ_WRITE,
        navItemId: 'insurance',
      },
      {
        moduleKey: 'inv.meal_services',
        label: 'Meal services',
        description: 'Stand-alone meal services.',
        actions: READ_WRITE,
        navItemId: 'meal-services',
      },
      {
        moduleKey: 'inv.suppliers',
        label: 'Suppliers',
        description: 'Inventory suppliers / vendors.',
        actions: READ_WRITE_DELETE,
        navItemId: 'suppliers',
      },
      {
        moduleKey: 'inv.airlines',
        label: 'Airlines',
        description: 'Airline master.',
        actions: READ_WRITE,
        navItemId: 'airlines',
      },
      {
        moduleKey: 'inv.destinations',
        label: 'Destinations',
        description: 'CRM destinations master (ADR-0041).',
        actions: READ_WRITE_DELETE,
        navItemId: 'destinations-master',
      },
      // 2026-07-24. Capability (no sidebar item): holders publish activity /
      // transfer IMAGE edits LIVE, skipping catalog approval. Everyone else's
      // media edits still go through the approval queue like any other change.
      // Admin-only by default (see ADMIN_VISIBLE_ONLY_MODULES) + granted to
      // website_manager; movable to any role from /roles.
      {
        moduleKey: 'inv.catalog_media_publish',
        label: 'Publish catalog media',
        description:
          'Publish activity / transfer image edits live without catalog approval. Non-holders still need approval, even for media.',
        actions: ['write'],
      },
    ],
  },

  // -----------------------------------------------------------------
  // Itinerary masters - typed lookups + pricing knobs.
  // -----------------------------------------------------------------
  {
    id: 'itinerary-masters',
    label: 'Itinerary masters',
    description: 'Typed lookups + pricing knobs that classify inventory.',
    modules: [
      {
        moduleKey: 'itin.meal_plans',
        label: 'Meal plans',
        description: 'Meal-plan codes (CP / MAP / AP / etc.).',
        actions: READ_WRITE,
        navItemId: 'meal-plans',
      },
      {
        moduleKey: 'itin.room_types',
        label: 'Room types',
        description: 'Single / Double / Triple etc.',
        actions: READ_WRITE,
        navItemId: 'room-types',
      },
      {
        moduleKey: 'itin.occupancy_types',
        label: 'Occupancy types',
        description: 'Adult / Child / Infant grouping.',
        actions: READ_WRITE,
        navItemId: 'occupancy-types',
      },
      {
        moduleKey: 'itin.vehicle_types',
        label: 'Vehicle types',
        description: 'Sedan / SUV / Coach etc.',
        actions: READ_WRITE,
        navItemId: 'vehicle-types',
      },
      {
        moduleKey: 'itin.locations',
        label: 'Locations',
        description: 'Geographic location master.',
        actions: READ_WRITE,
        navItemId: 'locations',
      },
      {
        moduleKey: 'itin.languages',
        label: 'Languages',
        description: 'Spoken / written language list.',
        actions: READ_WRITE,
        navItemId: 'languages',
      },
      {
        moduleKey: 'itin.cuisines',
        label: 'Cuisines',
        description: 'Cuisine tags.',
        actions: READ_WRITE,
        navItemId: 'cuisines',
      },
      {
        moduleKey: 'itin.markups',
        label: 'Markups',
        description: 'Pricing markup rules.',
        actions: READ_WRITE,
        navItemId: 'markups',
      },
      {
        moduleKey: 'itin.coupons',
        label: 'Coupons',
        description: 'Discount codes.',
        actions: READ_WRITE,
        navItemId: 'coupons',
      },
      {
        moduleKey: 'itin.discounts',
        label: 'Discounts',
        description: 'Percentage discount presets for itinerary pricing.',
        actions: READ_WRITE,
        navItemId: 'discounts',
      },
      {
        moduleKey: 'itin.additional_discounts',
        label: 'Upsell',
        description:
          'Upsell percentage presets that add cost on itinerary pricing (mutually exclusive with Discount).',
        actions: READ_WRITE,
        navItemId: 'additional-discounts',
      },
    ],
  },

  // -----------------------------------------------------------------
  // CRM masters - lead pipeline configuration.
  // -----------------------------------------------------------------
  {
    id: 'crm',
    label: 'Lead & CRM masters',
    description:
      'Lead-pipeline configuration: query types, sources, statuses, call statuses, lost reasons.',
    modules: [
      {
        moduleKey: 'crm.query_types',
        label: 'Query types',
        description: 'Top-level enquiry classification.',
        actions: READ_WRITE,
        navItemId: 'query-types',
      },
      {
        moduleKey: 'crm.query_sources',
        label: 'Query sources',
        description: 'Marketing source attribution.',
        actions: READ_WRITE,
        navItemId: 'query-sources',
      },
      {
        moduleKey: 'crm.query_sub_sources',
        label: 'Query sub-sources',
        description: 'Source sub-categories.',
        actions: READ_WRITE,
        navItemId: 'query-sub-sources',
      },
      {
        moduleKey: 'crm.query_mediums',
        label: 'Query mediums',
        description: 'Phone / WhatsApp / Email etc.',
        actions: READ_WRITE,
        navItemId: 'query-mediums',
      },
      {
        moduleKey: 'crm.query_statuses',
        label: 'Query statuses',
        description: 'Lead status master (Active / Lost / etc.).',
        actions: READ_WRITE,
        navItemId: 'query-statuses',
      },
      {
        moduleKey: 'crm.query_sub_statuses',
        label: 'Query sub-statuses',
        description: 'Sub-status under Active (Proposal / Hot / Warm).',
        actions: READ_WRITE,
        navItemId: 'query-sub-statuses',
      },
      {
        moduleKey: 'crm.call_statuses',
        label: 'Call statuses',
        description: 'Outcome categories for follow-up calls.',
        actions: READ_WRITE,
        navItemId: 'call-statuses',
      },
      {
        moduleKey: 'crm.call_sub_statuses',
        label: 'Call sub-statuses',
        description: 'Sub-status flags drive form behavior (ADR-0051).',
        actions: READ_WRITE,
        navItemId: 'call-sub-statuses',
      },
      {
        moduleKey: 'crm.lost_reasons',
        label: 'Lost reasons',
        description: 'Reason taxonomy for closed-lost leads.',
        actions: READ_WRITE,
        navItemId: 'lost-reasons',
      },
      {
        moduleKey: 'crm.trip_types',
        label: 'Trip types',
        description: 'FIT / Group / Cruise etc.',
        actions: READ_WRITE,
        navItemId: 'trip-types',
      },
      {
        moduleKey: 'crm.followup_reasons',
        label: 'Follow-up reasons',
        description: 'Reasons attached to scheduled calls.',
        actions: READ_WRITE,
        navItemId: 'followup-reasons',
      },
      {
        moduleKey: 'crm.branches',
        label: 'Branches',
        description: 'Office / branch master.',
        actions: READ_WRITE,
        navItemId: 'branches',
      },
      {
        moduleKey: 'crm.departments',
        label: 'Departments',
        description: 'Department / team master.',
        actions: READ_WRITE,
        navItemId: 'departments',
      },
      {
        moduleKey: 'crm.agent_targets',
        label: 'Agent targets',
        description: 'Per-agent monthly targets.',
        actions: READ_WRITE,
        navItemId: 'agent-targets',
      },
    ],
  },

  // -----------------------------------------------------------------
  // Global masters - geography + finance reference.
  // -----------------------------------------------------------------
  {
    id: 'global',
    label: 'Global masters',
    description: 'Geography + finance reference. Set once, almost never edited.',
    modules: [
      {
        moduleKey: 'geo.countries',
        label: 'Countries',
        description: 'Country master.',
        actions: READ_WRITE,
        navItemId: 'countries',
      },
      {
        moduleKey: 'geo.states',
        label: 'States',
        description: 'State / province master.',
        actions: READ_WRITE,
        navItemId: 'states',
      },
      {
        moduleKey: 'geo.cities',
        label: 'Cities',
        description: 'City master (CMS-curated).',
        actions: READ_WRITE,
        navItemId: 'cities-cms',
      },
      {
        moduleKey: 'geo.currencies',
        label: 'Currencies',
        description: 'Currency master + exchange rates.',
        actions: READ_WRITE,
        navItemId: 'currencies',
      },
    ],
  },

  // -----------------------------------------------------------------
  // Website - public-site CMS content + website-only masters.
  // -----------------------------------------------------------------
  {
    id: 'website',
    label: 'Website',
    description: 'Public-site CMS content and website-only masters.',
    modules: [
      {
        moduleKey: 'web.packages',
        label: 'Packages',
        description: 'Marketing packages on the public site.',
        actions: READ_WRITE_DELETE,
        navItemId: 'packages',
      },
      {
        moduleKey: 'web.blogs',
        label: 'Blogs',
        description: 'Blog post editor.',
        actions: READ_WRITE_DELETE,
        navItemId: 'blogs',
      },
      {
        moduleKey: 'web.blog_categories',
        label: 'Blog categories',
        description: 'Blog / story category master.',
        actions: READ_WRITE,
        navItemId: 'blog-categories',
      },
      {
        moduleKey: 'web.pages',
        label: 'Pages',
        description: 'Static site page editor.',
        actions: READ_WRITE_DELETE,
        navItemId: 'pages',
      },
      // 2026-07-09. Careers CMS page. Previously reused `web.pages.*`
      // for its sidebar gate; split into its own module so admins can
      // grant / revoke career-editor access independently.
      {
        moduleKey: 'web.career',
        label: 'Career',
        description: 'Careers CMS page (jobs list + application form).',
        actions: READ_WRITE_DELETE,
        navItemId: 'career',
      },
      // 2026-10-04. Internal Welcome Utazzer landing-page editor (content lives
      // in app_config, not the website CMS). Viewing the page is open to every
      // signed-in user via the ungated `me/welcome-page` read; this module gates
      // only the EDITOR - `read` opens it, `write` saves. Previously ADMIN-only;
      // split into a module so edit access can be granted to other roles from
      // the /roles matrix. website_manager + marketing_lead pick it up via the
      // website-group sweep in DEFAULT_ROLE_PERMISSIONS.
      {
        moduleKey: 'web.welcome',
        label: 'Welcome Utazzer editor',
        description: 'Internal Welcome Utazzer landing page (hero, sections, links).',
        actions: READ_WRITE,
        navItemId: 'welcome-page',
      },
      // 2026-07-14. Tourism boards + landing pages are WebsitePage rows like
      // Custom Pages, but each gets its own sidebar item and permission
      // module so a tourism-board editor can be granted access WITHOUT
      // handing them every custom page. The API resolves the module from the
      // row's pageType (see permissionModuleForPageType), so the isolation is
      // enforced server-side, not just in the sidebar.
      {
        moduleKey: 'web.tourism_boards',
        label: 'Tourism boards',
        description: 'Tourism-board landing pages (Discover <Destination>).',
        actions: READ_WRITE_DELETE,
        navItemId: 'tourism-boards',
      },
      {
        moduleKey: 'web.landing_pages',
        label: 'Landing pages',
        description: 'Marketing / campaign landing pages.',
        actions: READ_WRITE_DELETE,
        navItemId: 'landing-pages',
      },
      // 2026-08-03. Destination SEO guides ("Best time to visit <X>", "Best
      // places to visit in <X>"). Same WebsitePage plumbing as the two above,
      // one permission module each: the two guides are written by different
      // people in practice, so granting one must not hand over the other.
      {
        moduleKey: 'web.best_time_pages',
        label: 'Best time to visit',
        description: 'Destination "Best time to visit" SEO guides.',
        actions: READ_WRITE_DELETE,
        navItemId: 'best-time-to-visit',
      },
      {
        moduleKey: 'web.best_places_pages',
        label: 'Best places to visit',
        description: 'Destination "Best places to visit" SEO guides.',
        actions: READ_WRITE_DELETE,
        navItemId: 'best-places-to-visit',
      },
      {
        moduleKey: 'web.destination_pages',
        label: 'Destination pages',
        description: 'Public destination landing pages (hero, sections, SEO).',
        actions: READ_WRITE_DELETE,
        navItemId: 'destination-pages',
      },
      {
        moduleKey: 'web.category_pages',
        label: 'Category pages',
        description: 'Public category landing pages (hero, best sellers, SEO).',
        actions: READ_WRITE_DELETE,
        navItemId: 'category-pages',
      },
      {
        moduleKey: 'web.themes',
        label: 'Theme collections',
        description: 'Marketing theme tags + collections.',
        actions: READ_WRITE,
        navItemId: 'theme-collections',
      },
      {
        moduleKey: 'web.hero_inclusions',
        label: 'Hero inclusions',
        description: 'Hero-banner inclusion picklist.',
        actions: READ_WRITE,
        navItemId: 'hero-inclusions',
      },
      {
        moduleKey: 'web.inclusions_exclusions',
        label: 'Inclusions / exclusions',
        description: 'Itinerary inclusion / exclusion master.',
        actions: READ_WRITE,
        navItemId: 'inclusions-exclusions',
      },
      {
        moduleKey: 'web.navigation',
        label: 'Navigation',
        description: 'Public-site nav config.',
        actions: READ_WRITE,
        navItemId: 'navigation',
      },
    ],
  },

  // -----------------------------------------------------------------
  // System - admin-only controls.
  // -----------------------------------------------------------------
  {
    id: 'system',
    label: 'System',
    description: 'Admin-only controls: settings, secrets, users, roles, notifications.',
    modules: [
      {
        moduleKey: 'sys.settings',
        label: 'Settings (page access)',
        description:
          'Opens the Settings screen. Grant at least one tab module below as well - this code alone shows an empty page.',
        actions: READ_WRITE,
        navItemId: 'settings',
      },
      // 2026-08-04. The Settings screen is four unrelated surfaces behind one
      // code: public-site details, app branding, tax rates, and third-party
      // script injection. Tax and Scripts were additionally hard-gated on
      // `role === 'ADMIN'` in the page and `@Roles('ADMIN')` on their
      // endpoints, so they could not be delegated at all. One module per tab
      // lets an admin hand out exactly one of them from the /roles matrix.
      // `sys.settings.read` still gates the page itself (see above).
      {
        moduleKey: 'sys.settings_website',
        label: 'Settings · Website',
        description: 'Public-site details: logo, company profile, contact info.',
        actions: READ_WRITE,
        navItemId: 'settings',
      },
      {
        moduleKey: 'sys.settings_branding',
        label: 'Settings · Brand & Theme',
        description: 'App name, logo, colours and fonts (branding.* / theme.* config).',
        actions: READ_WRITE,
        navItemId: 'settings',
      },
      {
        moduleKey: 'sys.settings_tax',
        label: 'Settings · Tax & Pricing',
        description: 'GST and TCS rates applied across quotes and bookings (tax.* config).',
        actions: READ_WRITE,
        navItemId: 'settings',
      },
      {
        moduleKey: 'sys.settings_scripts',
        label: 'Settings · Scripts & tags',
        description:
          'Third-party script injection on the public site (analytics, pixels). Grant sparingly - this executes arbitrary code for every visitor.',
        actions: READ_WRITE,
        navItemId: 'settings',
      },
      {
        moduleKey: 'sys.settings_company',
        label: 'Settings · Company / Invoice',
        description:
          'The invoice seller identity (legal name, GSTIN, PAN, CIN, address, contact, logo, HSN, invoice prefix) rendered on every invoice and credit note.',
        actions: READ_WRITE,
        navItemId: 'settings',
      },
      {
        moduleKey: 'sys.notifications',
        label: 'Notification rules',
        description: 'Notification engine rules + templates (ADR-0048).',
        actions: READ_WRITE,
        navItemId: 'notifications',
      },
      {
        moduleKey: 'sys.secrets',
        label: 'Secrets',
        description: 'Integration secrets vault (ADR-0018).',
        actions: READ_WRITE,
        navItemId: 'secrets',
      },
      {
        moduleKey: 'sys.meta_capi',
        label: 'Meta CAPI',
        description: 'Meta Conversions API funnel-event push.',
        actions: READ_WRITE,
        navItemId: 'meta-capi',
      },
      {
        moduleKey: 'sys.users',
        label: 'Users',
        description: 'User accounts + role assignments.',
        actions: READ_WRITE_DELETE,
        navItemId: 'users',
      },
      {
        moduleKey: 'sys.roles',
        label: 'Roles & permissions',
        description: 'Role CRUD + permission grants.',
        actions: READ_WRITE_DELETE,
        navItemId: 'roles',
      },
    ],
  },
] as const;

/**
 * Flat view: every permission code in the registry. Useful for the
 * server-side seeder and the role-default mapper.
 */
export function listAllPermissionCodes(): string[] {
  const out: string[] = [];
  for (const group of PERMISSION_GROUPS) {
    for (const m of group.modules) {
      for (const a of m.actions) out.push(`${m.moduleKey}.${a}`);
    }
  }
  return out;
}

/**
 * Look up the permission code that gates a given sidebar nav item.
 * Returns the `.read` permission for the matching module, or `null`
 * if the nav item has no permission entry (e.g. the Dashboard).
 *
 * Used by the admin sidebar's `canAccess()` to gate items without
 * hardcoding codes in `nav-config.tsx`.
 */
export function permissionCodeForNavItem(
  navItemId: string,
  action: PermissionAction = 'read',
): string | null {
  for (const group of PERMISSION_GROUPS) {
    for (const m of group.modules) {
      if (m.navItemId === navItemId) {
        if (!m.actions.includes(action)) return null;
        return `${m.moduleKey}.${action}`;
      }
    }
  }
  return null;
}

/**
 * Backward-compat alias map. Each legacy code maps to the granular
 * permission codes that SHOULD be granted when a role still carries
 * the legacy one. Used by the migration's backfill step.
 *
 * Keys are LEGACY codes that existed before ADR-0056. Values are the
 * NEW codes a holder of the legacy code should also receive so they
 * keep their pre-cutover access.
 */
export const LEGACY_PERMISSION_ALIASES: Readonly<Record<string, ReadonlyArray<string>>> = {
  // master.* was the single all-or-nothing reference-data bucket. It
  // now fans out to every reference-data module across crm / inv /
  // itin / geo so any role with `master.read` keeps full reference
  // visibility.
  'master.read': [
    ...PERMISSION_GROUPS.filter((g) =>
      ['inventory', 'itinerary-masters', 'crm', 'global'].includes(g.id),
    ).flatMap((g) => g.modules.map((m) => `${m.moduleKey}.read`)),
  ],
  'master.write': [
    ...PERMISSION_GROUPS.filter((g) =>
      ['inventory', 'itinerary-masters', 'crm', 'global'].includes(g.id),
    ).flatMap((g) =>
      g.modules.filter((m) => m.actions.includes('write')).map((m) => `${m.moduleKey}.write`),
    ),
  ],
  'lead.read': ['ops.leads.read'],
  'lead.create': ['ops.leads.write'],
  'lead.update': ['ops.leads.write'],
  'customer.read': ['ops.customers.read'],
  'customer.create': ['ops.customers.write'],
  'customer.update': ['ops.customers.write'],
  'itinerary.read': ['ops.itineraries.read'],
  'itinerary.create': ['ops.itineraries.write'],
  'itinerary.update': ['ops.itineraries.write'],
  'blogs.write': ['web.blogs.write', 'web.blog_categories.write'],
  'pages.write': ['web.pages.write'],
  'navigation.write': ['web.navigation.write'],
  'settings.write': ['sys.settings.write'],
  'user.read': ['sys.users.read'],
};

/**
 * Look up a module entry by its `moduleKey`. Returns `null` when the
 * key is unknown (e.g. a mistyped decorator).
 */
export function findPermissionModule(moduleKey: string): PermissionModuleEntry | null {
  for (const group of PERMISSION_GROUPS) {
    for (const m of group.modules) {
      if (m.moduleKey === moduleKey) return m;
    }
  }
  return null;
}

/**
 * Map an HTTP verb to the coarse permission action the API guard checks.
 * HEAD/OPTIONS are treated as read-only.
 */
export function permissionActionForHttpMethod(method: string): PermissionAction {
  switch (method.toUpperCase()) {
    case 'GET':
    case 'HEAD':
    case 'OPTIONS':
      return 'read';
    case 'POST':
    case 'PUT':
    case 'PATCH':
      return 'write';
    case 'DELETE':
      return 'delete';
    default:
      return 'read';
  }
}

/**
 * Resolve `${moduleKey}.${action}` when the module supports that action.
 * When DELETE is requested but the module only declares read/write, falls
 * back to `.write` so hard-delete endpoints still require an editor grant.
 */
export function resolvePermissionCode(moduleKey: string, action: PermissionAction): string | null {
  const mod = findPermissionModule(moduleKey);
  if (!mod) return `${moduleKey}.${action}`;
  if (mod.actions.includes(action)) return `${moduleKey}.${action}`;
  if (action === 'delete' && mod.actions.includes('write')) return `${moduleKey}.write`;
  return null;
}

/* ------------------------------------------------------------------ *
 * Settings tabs (2026-08-04)                                         *
 * ------------------------------------------------------------------ */

/** Tab id on the Settings screen -> the permission module that owns it. */
export const SETTINGS_TAB_MODULES = {
  website: 'sys.settings_website',
  branding: 'sys.settings_branding',
  tax: 'sys.settings_tax',
  scripts: 'sys.settings_scripts',
  company: 'sys.settings_company',
} as const;

export type SettingsTabId = keyof typeof SETTINGS_TAB_MODULES;

/* ------------------------------------------------------------------ *
 * Lead Parsing System tabs (2026-10-02)                               *
 * ------------------------------------------------------------------ */

/** Tab of the Lead Parsing System page -> the permission module that owns it. */
export const LPS_TAB_MODULES = {
  inbound: 'ops.lps_inbound',
  deliveries: 'ops.lps_deliveries',
  connections: 'ops.lps_connections',
  website: 'ops.lps_website',
} as const;

export type LpsTabId = keyof typeof LPS_TAB_MODULES;

/** Display order of the tab strip; the first one held opens by default. */
export const LPS_TAB_ORDER: readonly LpsTabId[] = [
  'inbound',
  'deliveries',
  'connections',
  'website',
];

export const lpsTabReadCode = (tab: LpsTabId): string => `${LPS_TAB_MODULES[tab]}.read`;
export const lpsTabWriteCode = (tab: LpsTabId): string => `${LPS_TAB_MODULES[tab]}.write`;

/**
 * Sections of the Inbound leads tab (2026-10-04) -> the permission module
 * that owns each. Same shape as the Marketing report sections: a read
 * without `leadType` is the All section and needs the All grant.
 */
export const LPS_INBOUND_SECTION_MODULES = {
  all: 'ops.lps_inbound_all',
  inorganic: 'ops.lps_inbound_inorganic',
  organic: 'ops.lps_inbound_organic',
} as const;

export type LpsInboundSectionId = keyof typeof LPS_INBOUND_SECTION_MODULES;

/** Display order of the switch; the first one held opens by default. */
export const LPS_INBOUND_SECTION_ORDER: readonly LpsInboundSectionId[] = [
  'all',
  'inorganic',
  'organic',
];

export const lpsInboundSectionReadCode = (section: LpsInboundSectionId): string =>
  `${LPS_INBOUND_SECTION_MODULES[section]}.read`;

/** The section an inbound read belongs to, from its `leadType` query value. */
export const lpsInboundSectionForLeadType = (
  leadType: string | null | undefined,
): LpsInboundSectionId => (leadType === 'inorganic' || leadType === 'organic' ? leadType : 'all');

/**
 * Which settings module owns an AppConfig key.
 *
 * `PATCH /admin/config` carries Brand & Theme AND Tax & Pricing entries
 * through ONE endpoint, so those two tabs cannot be told apart by route - the
 * write gate has to be decided per key. Returns null for a key that belongs to
 * neither, and the caller falls back to the page-level `sys.settings.write` so
 * an unrecognised key can never be written by a tab-scoped holder.
 *
 * Shared so the server gate and any client-side check agree on the mapping.
 */
export function settingsModuleForConfigKey(key: string): string | null {
  if (key.startsWith('branding.') || key.startsWith('theme.')) {
    return SETTINGS_TAB_MODULES.branding;
  }
  if (key.startsWith('tax.')) return SETTINGS_TAB_MODULES.tax;
  if (key.startsWith('scripts.')) return SETTINGS_TAB_MODULES.scripts;
  return null;
}

/**
 * Write codes a set of AppConfig keys requires. One entry per distinct module
 * touched; an unrecognised key contributes `sys.settings.write`.
 */
export function settingsWriteCodesForConfigKeys(keys: readonly string[]): string[] {
  const codes = new Set<string>();
  for (const key of keys) {
    const moduleKey = settingsModuleForConfigKey(key);
    codes.add(moduleKey === null ? 'sys.settings.write' : `${moduleKey}.write`);
  }
  return [...codes].sort();
}

/* ------------------------------------------------------------------ *
 * Marketing report sections (2026-09-25)                             *
 * ------------------------------------------------------------------ */

/** Section of the Marketing report -> the permission module that owns it. */
export const MARKETING_REPORT_SECTION_MODULES = {
  all: 'ops.marketing_reports_all',
  inorganic: 'ops.marketing_reports_inorganic',
  organic: 'ops.marketing_reports_organic',
} as const;

export type MarketingReportSectionId = keyof typeof MARKETING_REPORT_SECTION_MODULES;

/** Display order of the section switch; the first one held opens by default. */
export const MARKETING_REPORT_SECTION_ORDER: readonly MarketingReportSectionId[] = [
  'all',
  'inorganic',
  'organic',
];

/** `ops.marketing_reports_<section>.read` for a section. */
export const marketingSectionReadCode = (section: MarketingReportSectionId): string =>
  `${MARKETING_REPORT_SECTION_MODULES[section]}.read`;

/**
 * Sections whose readers may see the COST side - ad spend, adset spend, cost
 * heads, Meta ad performance. Organic is left out on purpose: that section has
 * no costs, so an Organic-only reader must not reach them through the API
 * either.
 */
export const MARKETING_COST_SECTIONS: readonly MarketingReportSectionId[] = ['all', 'inorganic'];

/**
 * The section a report read belongs to, from its `leadType` query value.
 * Absent (or anything unrecognised) is the All section - every lead - which is
 * exactly what such a request returns, so it must need the All grant.
 */
export const marketingSectionForLeadType = (
  leadType: string | null | undefined,
): MarketingReportSectionId =>
  leadType === 'inorganic' || leadType === 'organic' ? leadType : 'all';

/* ------------------------------------------------------------------ *
 * Prerequisites (2026-09-25)                                         *
 * ------------------------------------------------------------------ */

/**
 * Codes that only do anything alongside another one: a section or a tab is
 * useless without the page it lives on. Marketing report sections need
 * `ops.marketing_reports.read` (it opens the page and the sidebar item);
 * Settings tabs need `sys.settings.read` the same way.
 *
 * The /roles matrices use this so an admin never has to know the rule: tick a
 * section and the page access comes with it; untick the page access and its
 * sections go with it. Without it, a user granted only "Inorganic" simply
 * never saw the Marketing report in their sidebar.
 */
export const PERMISSION_PREREQUISITES: Readonly<Record<string, string>> = Object.fromEntries([
  ...Object.values(MARKETING_REPORT_SECTION_MODULES).map(
    (moduleKey) => [`${moduleKey}.read`, 'ops.marketing_reports.read'] as const,
  ),
  ...Object.values(LPS_TAB_MODULES).flatMap((moduleKey) =>
    (['read', 'write'] as const).map(
      (a) => [`${moduleKey}.${a}`, 'ops.lead_parsing.read'] as const,
    ),
  ),
  ...Object.values(LPS_INBOUND_SECTION_MODULES).map(
    (moduleKey) => [`${moduleKey}.read`, 'ops.lps_inbound.read'] as const,
  ),
  ...Object.values(SETTINGS_TAB_MODULES).flatMap((moduleKey) =>
    (['read', 'write'] as const).map((a) => [`${moduleKey}.${a}`, 'sys.settings.read'] as const),
  ),
]);

export interface PrerequisiteReconciliation {
  /** The selection after the change, with prerequisites kept consistent. */
  next: Set<string>;
  /** Page codes added because a section / tab that needs them was ticked. */
  added: string[];
  /** Sections / tabs removed because the page code they need was unticked. */
  removed: string[];
}

/**
 * Keep a matrix selection consistent after one change (`prev` -> `next`).
 *
 *   - a newly ticked code whose prerequisite is not selected gets it added;
 *   - a newly unticked prerequisite takes every selected code that needs it
 *     with it - they would grant nothing on their own.
 *
 * `alsoHeld` is what the target already holds from elsewhere (the role
 * baseline behind a user's extras, a role's inherited codes). A prerequisite
 * held there is satisfied, so it is neither added nor used as a reason to
 * remove anything.
 *
 * Pure: returns a new set and never mutates its inputs.
 */
export function reconcilePermissionPrerequisites(
  prev: ReadonlySet<string>,
  next: ReadonlySet<string>,
  alsoHeld: ReadonlySet<string> = new Set(),
): PrerequisiteReconciliation {
  const result = new Set(next);
  const added: string[] = [];
  const removed: string[] = [];

  for (const code of next) {
    if (prev.has(code)) continue;
    const needs = PERMISSION_PREREQUISITES[code];
    if (needs && !result.has(needs) && !alsoHeld.has(needs)) {
      result.add(needs);
      added.push(needs);
    }
  }

  for (const code of prev) {
    if (next.has(code) || alsoHeld.has(code)) continue;
    for (const dependent of [...result]) {
      if (PERMISSION_PREREQUISITES[dependent] === code) {
        result.delete(dependent);
        removed.push(dependent);
      }
    }
  }

  return { next: result, added, removed };
}

/**
 * Whether an effective permission set satisfies a required code.
 * Exact match only - legacy alias expansion is handled at grant time
 * in the DB (ADR-0056 migration), not at UI check time.
 */
export function effectivePermissionSetHas(
  codes: ReadonlySet<string> | null | undefined,
  required: string,
): boolean {
  if (!codes) return false;
  return codes.has(required);
}

/**
 * Default permission set for the seeded system roles. Used by the
 * seed AND the migration's backfill (any new role gets these as a
 * baseline; existing roles get the new codes added on top of their
 * existing custom grants).
 *
 * Hierarchy:
 *   - admin       = everything (the seed inserts every code from
 *                   `listAllPermissionCodes()` directly, not from
 *                   this map)
 *   - ops         = full read/write on inventory, itin, crm, geo,
 *                   leads, customers, itineraries; read TBO; no
 *                   delete; no website; no system
 *   - sales_lead  = read on every master; full ops.leads/customers/
 *                   itineraries; no write on masters
 *   - sales_agent = read on every master; ops.leads/customers/
 *                   itineraries read+write; no delete anywhere
 *   - finance     = read-only across the board, plus payment/booking
 *                   actions which live outside this file
 *   - hr          = org masters (branches, departments) only. User
 *                   CRUD (`sys.users.*`) is ADMIN-only by policy
 *                   (2026-07-09) and NOT granted to hr any more.
 *   - read_only   = read-only on ops.* and reference data; no write
 *                   anywhere
 *   - marketing_lead = read-only on ops.*, full read/write on
 *                   web.*, full read/write on inv.destinations
 *   - content_editor = read on ops.*, full read/write on web.blogs,
 *                   web.blog_categories, web.pages, web.navigation
 */
// Modules whose sidebar-visibility code is admin-only by policy. The
// module exists in the matrix (admin can grant to any role from the
// /roles UI) but the group-iteration `ops` default excludes them so
// pre-existing `ops` role holders do not accidentally gain the new
// sidebar items when this file adds modules to the operations group.
// Add to this set when you introduce a new admin-only operations
// module that should stay opt-in.
const ADMIN_VISIBLE_ONLY_MODULES = new Set<string>([
  'ops.lead_approvals',
  'ops.customer_pdf',
  'ops.lead_parsing',
  'ops.lps_inbound',
  'ops.lps_inbound_all',
  'ops.lps_inbound_inorganic',
  'ops.lps_inbound_organic',
  'ops.lps_deliveries',
  'ops.lps_connections',
  'ops.lps_website',
  'ops.exit_unassigned',
  'ops.lead_reports',
  'ops.marketing_reports',
  'ops.marketing_reports_all',
  'ops.marketing_reports_inorganic',
  'ops.marketing_reports_organic',
  // Supplier payments - finance + admin by migration, never the ops fan-out.
  'ops.fund_requests',
  // CallSense call analytics is management/QA tooling - admin-only by default.
  'ops.callsense',
  // WhatsApp chat is granted explicitly (sales roles) - the ops/finance
  // fan-outs must not auto-gain customer chat access.
  'ops.whatsapp',
  // 2026-09-06. Website bookings carry passport numbers and the re-issue /
  // refund buttons move guest money. Admin-only unless granted by name.
  'ops.travel_bookings',
  'inv.catalog_media_publish',
  // 2026-07-29. Airline master ships admin-only by default: ADMIN holds it via
  // `listAllPermissionCodes()` and it stays in the /roles matrix so an admin can
  // grant it to any role, but the inventory-group fan-out below excludes it so
  // existing sales / finance / read-only holders don't auto-gain the sidebar item.
  'inv.airlines',
]);
const isFanOutModule = (moduleKey: string): boolean => !ADMIN_VISIBLE_ONLY_MODULES.has(moduleKey);

export const DEFAULT_ROLE_PERMISSIONS: Readonly<Record<string, ReadonlyArray<string>>> = {
  admin: listAllPermissionCodes(),
  ops: [
    ...PERMISSION_GROUPS.filter((g) =>
      ['operations', 'inventory', 'itinerary-masters', 'crm', 'global'].includes(g.id),
    ).flatMap((g) =>
      g.modules
        .filter((m) => isFanOutModule(m.moduleKey))
        .flatMap((m) => m.actions.filter((a) => a !== 'delete').map((a) => `${m.moduleKey}.${a}`)),
    ),
    // Ops could void contracts under the old hardcoded @Roles gate; the
    // fan-out above strips `delete`, so restore it explicitly.
    'ops.contracts.delete',
    'ops.converted_leads.delete',
  ],
  sales_lead: [
    'ops.leads.read',
    'ops.leads.write',
    'ops.customers.read',
    'ops.customers.write',
    'ops.itineraries.read',
    'ops.itineraries.write',
    'ops.fixed_departures.read',
    'ops.fixed_departures.write',
    'ops.tbo.read',
    'ops.exit_unassigned.read',
    'ops.exit_unassigned.write',
    'ops.whatsapp.read',
    'ops.whatsapp.write',
    'ops.contracts.read',
    'ops.contracts.write',
    'ops.contracts.delete',
    ...PERMISSION_GROUPS.filter((g) =>
      ['inventory', 'itinerary-masters', 'crm', 'global'].includes(g.id),
    ).flatMap((g) =>
      g.modules.filter((m) => isFanOutModule(m.moduleKey)).map((m) => `${m.moduleKey}.read`),
    ),
  ],
  sales_agent: [
    'ops.leads.read',
    'ops.leads.write',
    'ops.customers.read',
    'ops.customers.write',
    'ops.itineraries.read',
    'ops.itineraries.write',
    'ops.fixed_departures.read',
    'ops.fixed_departures.write',
    'ops.tbo.read',
    'ops.whatsapp.read',
    'ops.whatsapp.write',
    'ops.contracts.read',
    'ops.contracts.write',
    ...PERMISSION_GROUPS.filter((g) =>
      ['inventory', 'itinerary-masters', 'crm', 'global'].includes(g.id),
    ).flatMap((g) =>
      g.modules.filter((m) => isFanOutModule(m.moduleKey)).map((m) => `${m.moduleKey}.read`),
    ),
  ],
  // 2026-08-31. Finance is scoped tightly to the invoicing/accounting job. The
  // invoicing surface itself (invoices, credit notes, supplier AP, GST register)
  // is role-gated (`@Roles('ADMIN','FINANCE')`), so it needs no codes here. What
  // it DOES need as granular grants: create/edit the parties an invoice
  // references, the reference data the create-invoice form's dropdowns load, and
  // ONLY the Company / Invoice settings tab. Nothing else (no leads,
  // itineraries, fixed departures, itinerary masters, or other CRM/inventory
  // catalogs).
  finance: [
    // 2026-10-20. Supplier fund requests raised by operations (pay + upload the
    // payment proof). The seed REPLACES a role's grants from this list on every
    // run, so the migration grant alone would be wiped by a re-seed.
    'ops.fund_requests.read',
    'ops.fund_requests.write',
    // Parties an invoice references (picked + created from the form).
    'ops.customers.read',
    'ops.customers.write',
    'inv.suppliers.read',
    'inv.suppliers.write',
    // Reference data the create-invoice form's dropdowns load.
    'inv.airlines.read', // airline sector grid
    'geo.countries.read',
    'geo.states.read',
    'geo.cities.read',
    'geo.currencies.read',
    'crm.branches.read', // branch dropdown
    'crm.query_sources.read', // source dropdown
    // The Settings page + ONLY the Company / Invoice tab (the GST accounts).
    'sys.settings.read',
    'sys.settings_company.read',
    'sys.settings_company.write',
    // 2026-11-03. User management on the Services app (add / edit / delete
    // staff). `read` also drives the Services nav + the /users route gate. The
    // API guardrail (UsersService.assertFinanceManageAllowed) blocks a FINANCE
    // actor from creating / editing ADMIN users or assigning the ADMIN role, so
    // finance cannot self-escalate. Per-user permission grants + role CRUD
    // (sys.roles) stay ADMIN-only and are NOT listed here.
    'sys.users.read',
    'sys.users.write',
    'sys.users.delete',
  ],
  // 2026-07-09. `sys.users.*` is ADMIN-only by policy - no non-admin
  // role holds user CRUD by default. The API's UsersController is
  // also class-level `@Roles('ADMIN')`, so grants here would be inert
  // anyway; removing them from `hr` keeps the sidebar visibility and
  // matrix in agreement with the API guard.
  hr: ['crm.branches.read', 'crm.branches.write', 'crm.departments.read', 'crm.departments.write'],
  read_only: [
    'ops.leads.read',
    'ops.customers.read',
    'ops.itineraries.read',
    'ops.fixed_departures.read',
    'ops.contracts.read',
    ...PERMISSION_GROUPS.filter((g) =>
      ['inventory', 'itinerary-masters', 'crm', 'global'].includes(g.id),
    ).flatMap((g) =>
      g.modules.filter((m) => isFanOutModule(m.moduleKey)).map((m) => `${m.moduleKey}.read`),
    ),
  ],
  marketing_lead: [
    'ops.leads.read',
    'ops.customers.read',
    ...PERMISSION_GROUPS.filter((g) => g.id === 'website').flatMap((g) =>
      g.modules.flatMap((m) => m.actions.map((a) => `${m.moduleKey}.${a}`)),
    ),
    'inv.destinations.read',
    'inv.destinations.write',
    ...PERMISSION_GROUPS.filter((g) =>
      ['itinerary-masters', 'crm', 'global'].includes(g.id),
    ).flatMap((g) => g.modules.map((m) => `${m.moduleKey}.read`)),
  ],
  content_editor: [
    'ops.leads.read',
    'ops.customers.read',
    'web.blogs.read',
    'web.blogs.write',
    'web.blogs.delete',
    'web.blog_categories.read',
    'web.blog_categories.write',
    'web.pages.read',
    'web.pages.write',
    'web.pages.delete',
    // 2026-07-09. Career mirrors Pages for the content editor since
    // the two were on the same gate before web.career split off.
    'web.career.read',
    'web.career.write',
    'web.career.delete',
    // 2026-07-14. Same reasoning for the two page types split out of Pages:
    // a content editor could edit these as CUSTOM pages before, so keep them
    // able to. content_editor enumerates its codes (no group sweep), so new
    // website modules have to be listed here explicitly.
    'web.tourism_boards.read',
    'web.tourism_boards.write',
    'web.tourism_boards.delete',
    'web.landing_pages.read',
    'web.landing_pages.write',
    'web.landing_pages.delete',
    // 2026-08-03. Destination SEO guides - content work, same as the above.
    'web.best_time_pages.read',
    'web.best_time_pages.write',
    'web.best_time_pages.delete',
    'web.best_places_pages.read',
    'web.best_places_pages.write',
    'web.best_places_pages.delete',
    'web.destination_pages.read',
    'web.destination_pages.write',
    'web.destination_pages.delete',
    'web.category_pages.read',
    'web.category_pages.write',
    'web.category_pages.delete',
    'web.navigation.read',
    'web.navigation.write',
    ...PERMISSION_GROUPS.filter((g) =>
      ['itinerary-masters', 'crm', 'global'].includes(g.id),
    ).flatMap((g) => g.modules.map((m) => `${m.moduleKey}.read`)),
  ],
  // Full website-module manager: every website-group permission (packages,
  // pages, blogs, navigation, themes, hero/inclusions, destination pages,
  // category pages) + the destinations master. Category / destination
  // pages were originally role-gated via @Roles + nav requireRole; they
  // are now proper `web.category_pages.*` / `web.destination_pages.*`
  // permissions, so this role picks them up via the group sweep.
  website_manager: [
    ...PERMISSION_GROUPS.filter((g) => g.id === 'website').flatMap((g) =>
      g.modules.flatMap((m) => m.actions.map((a) => `${m.moduleKey}.${a}`)),
    ),
    'inv.destinations.read',
    'inv.destinations.write',
    // 2026-07-08. Activities + transfers were operator-managed via inv.*
    // roles only, but marketing owns the customer-facing catalog copy /
    // photos too - so website_manager gets full read+write on both masters
    // (delete stays admin-only to match how inv.destinations is scoped).
    'inv.activities.read',
    'inv.activities.write',
    'inv.transfers.read',
    'inv.transfers.write',
    // 2026-07-24. Website head (manager) publishes catalog image edits live,
    // bypassing approval. NOT granted to website_seo - the SEO exec's media
    // edits still go through approval like everyone else.
    'inv.catalog_media_publish.write',
    // Lead Parsing System: website form captures land here. Read-only is
    // enough to monitor Destination / Page / Message; write stays admin
    // unless granted from /roles. Inbound + website tabs only.
    'ops.lead_parsing.read',
    'ops.lps_inbound.read',
    'ops.lps_inbound_all.read',
    'ops.lps_website.read',
  ],
  // SEO: identical website permissions to website_manager (its edits will route
  // to website_manager for approval once the approval cycle ships).
  website_seo: [
    ...PERMISSION_GROUPS.filter((g) => g.id === 'website').flatMap((g) =>
      g.modules.flatMap((m) => m.actions.map((a) => `${m.moduleKey}.${a}`)),
    ),
    'inv.destinations.read',
    'inv.destinations.write',
    'inv.activities.read',
    'inv.activities.write',
    'inv.transfers.read',
    'inv.transfers.write',
    'ops.lead_parsing.read',
    'ops.lps_inbound.read',
    'ops.lps_inbound_all.read',
    'ops.lps_website.read',
  ],
  // ---------------------------------------------------------------
  // 2026-07-09 (ADR-0063). Six additional roles - `hr_manager`,
  // `hr_executive`, `operations_lead`, `operations_executive`, `visa`,
  // `ticketing` - are seeded into `roles` by migration but are
  // DELIBERATELY absent from this map. `seedRolePermissions()` in
  // `apps/api/prisma/seed.ts` treats any role listed here as
  // authoritative (deletes then rewrites its grants on every seed
  // run). Omitting them from the map means whatever an ADMIN grants
  // to those roles from the /roles matrix survives every re-seed.
  // Re-add a role to this map ONLY if you want its grants to be
  // code-managed again.
  // ---------------------------------------------------------------
};
